import { useForm } from 'react-hook-form';
import { useLocation } from 'wouter';
import type { ClubAPI } from '@shared/types/api';
import { CoverImageUpload } from '@/components/CoverImageUpload';
import { useConfirm } from '@/components/ConfirmModal';
import { useToast } from '@/components/ux/Toast';
import { AdminDialog } from '../AdminDialog';
import { MarkdownTextarea } from '../MarkdownTextarea';
import { useDeleteClub, useUpdateClub } from '../../api/useAdminClubs';

const CAPABILITIES: { key: ClubAPI.ClubCapability; label: string }[] = [
  { key: 'readForum', label: 'Read the forum' },
  { key: 'writeForum', label: 'Post & reply in the forum' },
  { key: 'votePolls', label: 'Vote in club polls' },
  { key: 'attendEvent', label: 'RSVP to in-person events' },
];

type SettingsForm = {
  name: string;
  slug: string;
  tagline: string;
  description: string;
  imageUrl: string;
  audience: ClubAPI.ClubAudience;
  status: ClubAPI.ClubStatus;
  eventThreshold: number;
  sortOrder: number;
  permissions: ClubAPI.ClubPermissions;
};

const fieldLabel = 'block text-xs font-medium text-neutral-500 mb-1';

/** Club settings: details, cover, audience, status, event threshold and permissions. */
export function ClubSettingsDialog({ club, onClose }: { club: ClubAPI.Club; onClose: () => void }) {
  const toast = useToast();
  const { confirm } = useConfirm();
  const [, navigate] = useLocation();
  const updateClub = useUpdateClub();
  const deleteClub = useDeleteClub();
  const onError = (err: any) => toast.error(err?.message || 'Something went wrong');

  const form = useForm<SettingsForm>({
    defaultValues: {
      name: club.name,
      slug: club.slug,
      tagline: club.tagline ?? '',
      description: club.description ?? '',
      imageUrl: club.imageUrl ?? '',
      audience: club.audience,
      status: club.status,
      eventThreshold: club.eventThreshold,
      sortOrder: club.sortOrder,
      permissions: club.permissions,
    },
  });

  const save = form.handleSubmit((v) => {
    updateClub.mutate(
      {
        id: club.id,
        data: {
          ...v,
          tagline: v.tagline || null,
          description: v.description || null,
          imageUrl: v.imageUrl || null,
          eventThreshold: Number(v.eventThreshold),
          sortOrder: Number(v.sortOrder),
        },
      },
      { onSuccess: () => { toast.success('Club saved'); onClose(); }, onError },
    );
  });

  const handleDelete = async () => {
    const ok = await confirm({
      title: 'Delete club',
      message: `Delete "${club.name}"? Members, posts and club polls are deleted too. Consider archiving instead.`,
      confirmText: 'Delete',
      cancelText: 'Cancel',
      type: 'danger',
    });
    if (!ok) return;
    deleteClub.mutate(club.id, { onSuccess: () => navigate('/clubs'), onError });
  };

  return (
    <AdminDialog
      open
      onClose={onClose}
      title="Club settings"
      size="lg"
      footer={
        <>
          <button type="button" onClick={handleDelete} className="text-sm text-red-600 hover:underline mr-auto">
            Delete club
          </button>
          <button type="button" onClick={onClose} className="btn btn-ghost btn-sm">Cancel</button>
          <button type="button" onClick={save} disabled={updateClub.isPending} className="btn btn-primary btn-sm">
            Save
          </button>
        </>
      }
    >
      <form onSubmit={save} className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
        <div className="md:row-span-2">
          <span className={fieldLabel}>Cover image</span>
          <CoverImageUpload
            shape="square"
            value={form.watch('imageUrl')}
            onChange={(url) => form.setValue('imageUrl', url, { shouldDirty: true })}
            onError={(msg) => toast.error(msg)}
          />
        </div>
        <div className="space-y-4">
          <label className="block">
            <span className={fieldLabel}>Name</span>
            <input className="input input-bordered w-full" {...form.register('name', { required: true })} />
          </label>
          <label className="block">
            <span className={fieldLabel}>Slug</span>
            <input className="input input-bordered w-full" {...form.register('slug', { required: true })} />
          </label>
          <label className="block">
            <span className={fieldLabel}>Tagline</span>
            <input className="input input-bordered w-full" {...form.register('tagline')} />
          </label>
        </div>
        <div className="md:col-span-2">
          <span className={fieldLabel}>Description</span>
          <MarkdownTextarea
            registration={form.register('description')}
            value={form.watch('description')}
            onChange={(v) => form.setValue('description', v, { shouldDirty: true })}
          />
        </div>
        <label>
          <span className={fieldLabel}>Audience</span>
          <select className="select select-bordered w-full" {...form.register('audience')}>
            <option value="sapphic">Sapphic</option>
            <option value="gay">Gay</option>
            <option value="everyone">Everyone</option>
          </select>
        </label>
        <label>
          <span className={fieldLabel}>Status</span>
          <select className="select select-bordered w-full" {...form.register('status')}>
            <option value="draft">Draft (hidden)</option>
            <option value="unopened">Unopened (teaser, joinable)</option>
            <option value="active">Active</option>
            <option value="archived">Archived</option>
          </select>
        </label>
        <label>
          <span className={fieldLabel}>Members per city for an event</span>
          <input type="number" min={1} className="input input-bordered w-full" {...form.register('eventThreshold', { valueAsNumber: true })} />
        </label>
        <label>
          <span className={fieldLabel}>Sort order</span>
          <input type="number" className="input input-bordered w-full" {...form.register('sortOrder', { valueAsNumber: true })} />
        </label>
        <div className="md:col-span-2">
          <span className={fieldLabel}>Who can… (joining is always free)</span>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
            {CAPABILITIES.map(({ key, label }) => (
              <label key={key} className="flex items-center justify-between gap-2 border rounded-lg px-3 py-2 dark:border-neutral-700">
                <span className="dark:text-neutral-200">{label}</span>
                <select className="select select-bordered select-sm" {...form.register(`permissions.${key}`)}>
                  <option value="member">Members</option>
                  <option value="subscriber">Subscribers</option>
                </select>
              </label>
            ))}
          </div>
        </div>
      </form>
    </AdminDialog>
  );
}
